import {
  faCircleExclamation,
  faClipboard,
  faSearch,
  faWifi,
  faWifiStrong,
  faList,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Accordion, B2Button } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormControl } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { B2Form, B2InputGroup } from "src/shared/@hdb/biome-2";
import {
  getAssetsByBlockLevelAndTypeService,
  getAssetsByBlockService,
  getBlocksBySiteCodeService,
  getLevelsByBlockCode,
  getSitesByTcCodeService,
} from "../../../../core/api/services/dashbaorad-details/location-block.services";
import ModalComponent from "../../../../core/components/modal/modal.component";
import TableComponent from "../../../../core/components/table/table.component";
import {
  AssetFilterValues,
  AssetStatus,
} from "../../../../core/enums/asset-enums";
import ContentHeader from "../../../../shared/content-header/content-header.component";
import SkeletonComponent from "../../../../shared/custom-spinner/skeleton.component";
import TableSort from "../../../../shared/helpers/table-sort";
import capitalizeFirst from "../../../../shared/helpers/upper-case";
import {
  assetsByBlockAndType,
  getSearchedAssets,
  setSearchedAssets,
  setSelectedAssetType,
  addSelectedAsset,
  removeSelectedAsset,
  clearSelectedAssets,
  toggleSelectionModal,
  toggleAnalyticsModal,
  setAnalyticsDate,
} from "../../../../store/slices/asset.slice";
import { setBlockOptionsData } from "../../../../store/slices/location-block.slice";
import { setSiteOptionsData } from "../../../../store/slices/location-site.slice";
import SearchBarComponent from "../../../admin/components/search-bar/search-bar.component";
import lightOff from "../../../dashboard/components/dashboard-floor-plan/canvas/images/off-initial.png";
import lightOn from "../../../dashboard/components/dashboard-floor-plan/canvas/images/on-online-filled.png";
import "../../asset.scss";
import AssetMainFilterComponent from "./components/asset-main-filter.component";
import AssetAccordionBody from "./asset-main-accordion-body.component";
import AssetBreadcrumb from "./components/asset-breadcrumb.component";
import AssetSelectionModal from "./components/asset-selection-modal.component";
import AssetAnalyticsModal from "./components/asset-analytics-modal.component";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import * as moment from "moment";

const mergeAssetsByType = (
  previousGroups: any[],
  assetType: string,
  assets: any[],
  totalCount?: number,
  blockCode?: string
) => {
  const groups = previousGroups || [];
  const typedAssets = (Array.isArray(assets) ? assets : []).map((asset: any) => ({
    ...asset,
    assetType,
    blockCode: blockCode ?? asset?.blockCode,
  }));
  const existingIndex = groups.findIndex(
    (group: any) => group?.label === assetType
  );
  const existingGroup = groups[existingIndex];
  const existingAssets = existingGroup?.assets || [];
  const assetCodes = new Set(
    existingAssets
      .map((asset: any) => asset?.assetCode)
      .filter((assetCode: string) => assetCode)
  );
  const uniqueAssets = typedAssets.filter((asset: any) => {
    if (!asset?.assetCode || assetCodes.has(asset.assetCode)) return false;
    assetCodes.add(asset.assetCode);
    return true;
  });

  if (existingIndex === -1) {
    return [
      ...groups,
      {
        label: assetType,
        totalCount: totalCount ?? 0,
        assets: uniqueAssets,
      },
    ];
  }

  const nextGroups = [...groups];
  nextGroups[existingIndex] = {
    ...existingGroup,
    label: assetType,
    totalCount: totalCount ?? existingGroup?.totalCount ?? 0,
    assets: [...existingAssets, ...uniqueAssets],
  };

  return nextGroups;
};

const AssetMainComponent = () => {
  const [showSearchBox, setShowSearchBox] = useState<any>(false);
  const [noResult, setNoResult] = useState(false);
  const [isSearchBarEmpty, setIsSearchBarEmpty] = useState(true);
  const [isAlreadyDispatched, setIsAlreadyDispatched] = useState(false);

  const [blockSelected, setBlockSelected] = useState<string>("");
  const [assetTypeSelected, setAssetTypeSelected] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");

  const [totalCount, setTotalCount] = useState<number>(10);

  const [PageNo, setPageNo] = useState<number>(1);
  const PageSize = 15;
  const [assetGroupModalOpen, setAssetGroupModalOpen] =
    useState<boolean>(false);

  const [mergedAssetsData, setMergedAssetsData] = useState<any[]>([]);
  const mergedAssetsDataRef = useRef(mergedAssetsData);
  mergedAssetsDataRef.current = mergedAssetsData;
  const [hasMoreAssetsByType, setHasMoreAssetsByType] = useState<
    Record<string, boolean>
  >({});
  const [isLoadingMoreAssetsByType, setIsLoadingMoreAssetsByType] = useState<
    Record<string, boolean>
  >({});
  const [isInitialAssetsLoading, setIsInitialAssetsLoading] = useState<boolean>(false);

  const [SortBy, setSortBy] = useState<any>(capitalizeFirst("assetCode"));
  const [SortDirection, setSortDirection] = useState<any>("asc");
  const [tCSelected, setTCSelected] = useState({ tcCode: "", tcName: "" });
  const [filterSelectedSite, setFilterSelectedSite] = useState<any>({
    siteCode: "",
    siteName: "",
  });
  const [filterSelectedBlock, setFilterSelectedBlock] = useState<any>({
    blockCode: "",
    blockName: "",
  });
  const [filterSelectedLevel, setFilterSelectedLevel] = useState<any>({
    levelCode: null,
    levelName: null,
  });
  const [siteCode, setSiteCode] = useState<any>(null);
  const [sitesByTCCode, setSitesByTCCode] = useState<any>(null);
  const [levelOptions, setLevelOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [levelAccordionReset, setLevelAccordionReset] = useState(0);

  const [searchValue, setSearchValue] = useState("");

  const [columns, setColumns] = useState([
    {
      title: "Asset ID",
      label: "assetCode",
      isSorted: true,
      type: "link",
      link: "/asset",
      linkableField: "assetCode",
      width: "20rem",
    },
    {
      title: "Display Name",
      label: "sensorDisplayName",
      width: "13rem",
    },
    {
      title: "Address",
      label: "address",
    },
    {
      title: "Level",
      label: "floorCode",
    },
    {
      title: "Status",
      label: "status",
    },
    {
      title: "Last Online",
      label: "lastOnline",
      type: "datetime",
    },
  ]);
  const dispatch = useDispatch();

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const assetTypePageRef = useRef<Record<string, number>>({});

  const isLoadingSelectedAssetsByBlockAndTypeData = useSelector(
    (state: any) => {
      return state.asset.isLoadingSelectedAssetsByBlockAndType;
    }
  );

  const selectedAssetsByBlockAndType = useSelector((state: any) => {
    return state.asset.selectedAssetsByBlockAndType;
  });

  const searchedAssets = useSelector((state: any) => {
    return state.asset.searchedAssets;
  });

  const selectedAssets = useSelector((state: any) => {
    return Array.isArray(state.asset.selectedAssets) ? state.asset.selectedAssets : [];
  });

  const isSelectionModalOpen = useSelector((state: any) => {
    return state.asset.isSelectionModalOpen;
  });

  const isAnalyticsModalOpen = useSelector((state: any) => {
    return state.asset.isAnalyticsModalOpen;
  });

  const analyticsDate = useSelector((state: any) => {
    return state.asset.analyticsDate || new Date().toISOString().split("T")[0];
  });

  const [selectionPage, setSelectionPage] = useState(1);
  const selectionPageSize = 10;

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(selectedAssets.length / selectionPageSize));
    setSelectionPage((currentPage) => Math.min(currentPage, lastPage));
  }, [selectedAssets.length]);

  useEffect(() => {
    // Reset searchedAssets to an empty array on page load
    dispatch(setSearchedAssets([]));
  }, [dispatch]);

  useEffect(() => {
    if (isSearchBarEmpty) {
      setShowSearchBox(false);
      setNoResult(false);
    }
  }, [isSearchBarEmpty]);

  useEffect(() => {
    if (searchedAssets?.length) {
      setShowSearchBox(true);
      setNoResult(false);
    }
  }, [searchedAssets]);

  useEffect(() => {
    if (isAlreadyDispatched && searchedAssets?.length === 0) {
      setNoResult(true);
    }
  }, [isAlreadyDispatched, searchedAssets]);

  const { data: sitesByTcCodeResponse } =
    useQuery({
      queryKey: ["getSitesByTcCodeService", tCSelected?.tcCode],
      queryFn: ({ signal }) =>
        getSitesByTcCodeService(tCSelected?.tcCode, signal),
    });

  const {
    data: blocksBySiteCodeResponse,
    isLoading: isLoadingBlocksBySiteCode,
  } = useQuery({
    queryKey: ["getBlocksBySiteCodeService", siteCode],
    queryFn: ({ signal }) => getBlocksBySiteCodeService(siteCode, signal),
  });

  useEffect(() => {
    if (!blockSelected || !selectedLevel) {
      setMergedAssetsData([]);
      setIsInitialAssetsLoading(false);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    assetTypePageRef.current = {};
    setHasMoreAssetsByType({});
    setIsLoadingMoreAssetsByType({});

    const fetchAssets = async () => {
      setIsInitialAssetsLoading(true);
      setMergedAssetsData([]);

      try {
        const data = await getAssetsByBlockService(
          blockSelected,
          selectedLevel,
          controller.signal,
          1,
          PageSize
        );

        if (isCancelled) return;

        const groups = data || [];
        const nextPageByType: Record<string, number> = {};
        const hasMore: Record<string, boolean> = {};

        groups.forEach((group: any) => {
          const totalCount = group?.totalCount ?? group?.assets?.length ?? 0;
          nextPageByType[group?.label] = 2;
          hasMore[group?.label] =
            (group?.assets?.length || 0) < totalCount;
        });

        assetTypePageRef.current = nextPageByType;
        setMergedAssetsData(groups);
        setHasMoreAssetsByType(hasMore);
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to fetch assets:", error);
        }
      } finally {
        if (!isCancelled) {
          setIsInitialAssetsLoading(false);
        }
      }
    };

    fetchAssets();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [blockSelected, selectedLevel, PageSize]);

  const fetchNextPage = useCallback(async (assetType?: string) => {
    if (!assetType || !blockSelected) return;
    if (
      isLoadingMoreAssetsByType[assetType] ||
      hasMoreAssetsByType[assetType] === false
    ) return;

    const nextPage = assetTypePageRef.current[assetType] || 2;
    const controller = new AbortController();

    setIsLoadingMoreAssetsByType((previous) => ({
      ...previous,
      [assetType]: true,
    }));

    try {
      const response = await getAssetsByBlockLevelAndTypeService(
        blockSelected,
        selectedLevel,
        assetType,
        controller.signal,
        nextPage,
        PageSize,
        SortBy,
        SortDirection
      );
      const assets = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const nextMerged = mergeAssetsByType(
        mergedAssetsDataRef.current,
        assetType,
        assets,
        response?.numberOfRecords,
        blockSelected
      );
      const nextGroup = nextMerged.find(
        (group: any) => group?.label === assetType
      );
      const totalCount =
        response?.numberOfRecords ?? nextGroup?.totalCount ?? 0;
      const loadedCount = nextGroup?.assets?.length || 0;

      setMergedAssetsData(nextMerged);
      assetTypePageRef.current[assetType] = nextPage + 1;
      setHasMoreAssetsByType((previous) => ({
        ...previous,
        [assetType]: loadedCount < totalCount,
      }));
    } catch (error) {
      console.error("Failed to fetch next page:", error);
    } finally {
      setIsLoadingMoreAssetsByType((previous) => ({
        ...previous,
        [assetType]: false,
      }));
    }
  }, [
    blockSelected,
    selectedLevel,
    hasMoreAssetsByType,
    isLoadingMoreAssetsByType,
    PageSize,
    SortBy,
    SortDirection,
  ]);

  useEffect(() => {
    dispatch(setSiteOptionsData([]));
    dispatch(setBlockOptionsData([]));
  }, []);

  useEffect(() => {
    if (filterSelectedSite?.siteCode) {
      const selectedSite = sitesByTcCodeResponse?.filter(
        (option: any) => option?.siteCode === filterSelectedSite?.siteCode
      );

      setSitesByTCCode(selectedSite);

      onClickedSite(filterSelectedSite?.siteCode);
    } else {
      setSitesByTCCode(sitesByTcCodeResponse);
    }
  }, [
    sitesByTcCodeResponse,
    filterSelectedSite,
  ]);

  const getSelectedFilterInfo = (filterValues: AssetFilterValues) => {
    setTCSelected(filterValues?.tc);
    setFilterSelectedSite(filterValues?.site);
    setFilterSelectedBlock(filterValues?.block);
    setFilterSelectedLevel(filterValues?.level);

    setBlockSelected(filterValues?.block?.blockCode || "");
    setLevelAccordionReset((v) => v + 1);
    setSelectedLevel(filterValues?.level?.levelCode || "");
  };

  const onClickedSite = (selectedSite: any) => {
    setSiteCode(selectedSite);
  };

  const onClickedBlock = async (selectedBlock: any) => {
    const blockCode = typeof selectedBlock === "string" ? selectedBlock : selectedBlock?.blockCode;
    if (blockCode === blockSelected) return;

    setBlockSelected(blockCode);
    setFilterSelectedLevel({ levelCode: null, levelName: null });
    setLevelAccordionReset((v) => v + 1);

    try {
      const levels = await getLevelsByBlockCode(blockCode, new AbortController().signal);
      const levelOptions = (levels || []).map((level: any) => ({
        label: level.displayLevel,
        value: level.displayLevel,
      }));
      setLevelOptions(levelOptions);
    } catch (error) {
      console.error("Failed to fetch levels for block:", error);
    }
  };

  const handleToggleAsset = (assetCode: string) => {
    if (selectedAssets.includes(assetCode)) {
      dispatch(removeSelectedAsset(assetCode));
    } else {
      dispatch(addSelectedAsset(assetCode));
    }
  };

  const handleRemoveAsset = (assetCode: string) => {
    dispatch(removeSelectedAsset(assetCode));
  };

  const handleClearAllAssets = () => {
    dispatch(clearSelectedAssets());
    setSelectionPage(1);
  };

  const handleOpenSelectionModal = () => {
    dispatch(toggleSelectionModal(true));
    setSelectionPage(1);
  };

  const handleCloseSelectionModal = () => {
    dispatch(toggleSelectionModal(false));
  };

  const handleOpenAnalyticsModal = () => {
    const today = new Date().toISOString().split("T")[0];
    dispatch(setAnalyticsDate(today));
    dispatch(toggleAnalyticsModal(true));
  };

  const handleCloseAnalyticsModal = () => {
    dispatch(toggleAnalyticsModal(false));
  };

  const handleAnalyticsDateChange = (date: string) => {
    dispatch(setAnalyticsDate(date));
  };

  const handleAnalyticsCompare = () => {
    // The useQuery in the analytics modal will auto-refetch when date changes
  };

  const handleLevelSelect = (levelCode: string) => {
    setSelectedLevel(levelCode);
    setFilterSelectedLevel({
      levelCode,
      levelName: levelCode,
    });
  };

  const onClickViewMore = (pTitle: string, count: number) => {
    setPageNo(1);
    setAssetGroupModalOpen(true);
    setAssetTypeSelected(pTitle);
    setTotalCount(count);
    dispatch(
      assetsByBlockAndType({
        blockCode: blockSelected,
        assetType: pTitle,
        PageNumber: PageNo,
        PageSize: PageSize,
        SortBy: SortBy,
        SortDirection: SortDirection,
      })
    );
  };

  const searchByAsset = (assetCode: any) => {
    setShowSearchBox(false);
    setNoResult(false);
    setIsAlreadyDispatched(false);

    dispatch(setSearchedAssets([]));

    window.open(`${window.location.origin}/asset/${btoa(assetCode)}`, "_blank");
  };

  const handleClickOutside = (event: any) => {
    if (
      searchBoxRef.current &&
      !searchBoxRef.current.contains(event.target as Node)
    ) {
      setShowSearchBox(false);
      setNoResult(false);
      setIsAlreadyDispatched(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (blockSelected && assetTypeSelected && assetGroupModalOpen) {
      dispatch(
        assetsByBlockAndType({
          blockCode: blockSelected,
          assetType: assetTypeSelected,
          PageNumber: PageNo,
          PageSize: PageSize,
          SortBy: SortBy,
          SortDirection: SortDirection,
        })
      );
    }
  }, [dispatch, PageNo, SortBy, SortDirection]);

  const searchResultsHeight = Math.min(searchedAssets?.length * 70, 400);

  const handleCloseModal = () => {
    setAssetGroupModalOpen(false);
    setPageNo(1);
  };

  const handleChangeSort = useCallback(
    async (sortBy: string, sortDirection: any) => {
      if (!sortBy || !sortDirection) return;
      setSortBy(sortBy);
      setSortDirection(sortDirection);

      const copyColumns: any = await TableSort(columns, sortBy);
      setColumns(copyColumns);
    },
    []
  );

  const handleChangePage = (page: number): void => {
    setPageNo(page);
  };

  const handleClear = () => {
    setSearchValue("");
  };

  const handleKeyUp = (e: any) => {
    if (e.keyCode === 8 && !searchValue) {
      handleClear();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    if (e) e.preventDefault();

    if (searchValue) {
      dispatch(
        assetsByBlockAndType({
          blockCode: blockSelected,
          assetType: assetTypeSelected,
          PageNumber: PageNo,
          PageSize,
          SortBy,
          SortDirection,
          searchText: searchValue,
        })
      );
    }
  };

  const navigateToFault = (tc: string, site: string, block: string) => {
    const url = new URL(`${window.location.origin}/fault`);
    url.searchParams.append("tc", tc);
    url.searchParams.append("site", site);
    url.searchParams.append("block", block);
    window.open(url.toString(), "_blank");
  };

  // Detect page refresh/closing
  /*   useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("postRefreshRedirect", location.pathname);
      navigate("/", { replace: true });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [location.pathname]); */

  const navigateToFloorplanView = (asset: any) => {
    const blockCode = asset?.blockCode;
    const url = new URL(
      `${window.location.origin}/dashboard/${btoa(
        blockCode
      )}/floorplan/${encodeURIComponent(asset?.blockToFloorCode)}`
    );
    url.searchParams.append("assetCode", btoa(asset?.assetCode));
    window.open(url.toString(), "_blank");
  };

  //   const navigateToFloorplanView = (asset: any) => {
  //   const blockCode = asset?.blockCode;
  //   const floorplanPath = `/dashboard/${btoa(blockCode)}/floorplan/${encodeURIComponent(asset?.floor)}?assetCode=${asset?.level}`;
  //   navigate(floorplanPath);
  // };

  return (
    <>
      <Helmet>
        <title>Asset | SLP</title>
      </Helmet>
      <div>
        <div className="header-container">
          <div className="title-container">
            <ContentHeader pageTitle="Asset" />
          </div>
        </div>

        <div className="page">
          <div className="page-container mt-4">
            {/* search bar section */}
            <div
              className="d-flex gap-4 align-items-center mb-3 flex-wrap flex-md-nowrap justify-content-end"
            >
              <div
                className="asset-search-bar"
                style={{
                  position: "relative",
                }}
              >
                <SearchBarComponent
                  placeholder="Search by Asset (Type three or more characters to search)"
                  methodToDispatch={getSearchedAssets}
                  isDynamicSearch={true}
                  setIsAlreadyDispatched={setIsAlreadyDispatched}
                  setIsSearchBarEmpty={setIsSearchBarEmpty}
                  isAssetModule={true}
                />

                {searchedAssets?.length && showSearchBox ? (
                  <div
                    ref={searchBoxRef}
                    className="shadow-sm"
                    style={{
                      backgroundColor: "#ffffff",
                      height: `${searchResultsHeight}px`,
                      overflowY: "auto",
                      position: "absolute",
                      top: 40,
                      left: 26,
                      zIndex: 999,
                      width: "95%",
                      borderBottomRightRadius: "15px", // Curved bottom right corner
                      borderBottomLeftRadius: "15px", // Curved bottom left corner
                    }}
                  >
                    {searchedAssets?.map((asset: any) => (
                      <button
                        type="button"
                        key={asset?.assetCode}
                        className="search-item"
                      // onClick={() => searchByAsset(asset?.assetCode)}
                      >
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", flex: 1 }}>
                            <input
                              className="form-check-input mt-1"
                              type="checkbox"
                              checked={selectedAssets.includes(asset?.assetCode)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleAsset(asset?.assetCode);
                              }}
                              style={{ cursor: "pointer" }}
                            />
                            <div onClick={() => searchByAsset(asset?.assetCode)} style={{ flex: 1 }}>
                              <div className="mb-1">{asset?.assetCode}</div>
                              <div className="info">
                                <span className="fw-bold info-level-color">
                                  Display Name:{" "}
                                </span>
                                {asset?.sensorDisplayName}{" "}
                                <span className="fw-bold ms-2 info-level-color">
                                  Address:{" "}
                                </span>
                                {asset?.address}{" "}
                                <span className="fw-bold ms-2 info-level-color">
                                  Level:{" "}
                                </span>
                                {asset?.level}
                                {asset?.lastOnline ? (
                                  <>
                                    <span className="fw-bold ms-2 info-level-color">
                                      Last Online:{" "}
                                    </span>
                                    {moment(asset?.lastOnline).format("DD-MM-YYYY HH:mm:ss")}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="d-flex align-items-center gap-2 flex-column flex-sm-row">
                              {/* <div>
                                {asset?.status == AssetStatus.ONLINE ? (
                                  <div
                                    style={{
                                      color: "1976D2",
                                    }}
                                  >
                                    {asset?.assetType === "Gateway" ? (
                                      <FontAwesomeIcon
                                        icon={faWifi}
                                        className="h5 ms-2"
                                        title="Online"
                                      />
                                    ) : (
                                      <img
                                        className="h5"
                                        src={lightOn}
                                        alt="Light On"
                                        title="Light On"
                                        width="25px"
                                        height="25px"
                                      />
                                    )}
                                  </div>
                                ) : asset?.status == AssetStatus.OFFLINE ? (
                                  <div
                                    style={{
                                      color: "#B36500",
                                      display: "flex",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "relative",
                                      }}
                                    >
                                      {asset?.assetType === "Gateway" ? (
                                        <>
                                          <FontAwesomeIcon
                                            icon={faWifiStrong}
                                            className="h5"
                                            title="Offline"
                                          />
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "0px",
                                              left: "10.3px",
                                              fontSize: "16px",
                                            }}
                                          >
                                            !
                                          </div>
                                        </>
                                      ) : (
                                        <img
                                          className="h5"
                                          src={lightOff}
                                          alt="Light Off"
                                          title="Light Off"
                                          width="25px"
                                          height="25px"
                                        />
                                      )}
                                    </div>
                                  </div>
                                ) : asset?.status == AssetStatus.INITIAL ? (
                                  <div
                                    style={{
                                      color: "rgb(143 143 143)",
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faWifi}
                                      className="h5 ms-2"
                                      title="Initial"
                                    />
                                  </div>
                                ) : null}
                              </div> */}

                              {/* <div style={{ display: "flex", color: "red" }}>
                                <FontAwesomeIcon
                                  icon={faCircleExclamation}
                                  className="h5 ms-2"
                                  title="Fault"
                                />
                                <small
                                  style={{
                                    marginTop: "10px",
                                  }}
                                  className="fs-6"
                                >
                                  {asset?.totalFaults || 0}{" "}
                                </small>
                              </div> */}

                              <div
                                style={{ display: "flex", marginBottom: "8px" }}
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="fs-7"
                                  title={
                                    asset?.blockToFloorId != null
                                      ? "Floor Plan"
                                      : "Asset Not Annotated "
                                  }
                                  style={{
                                    cursor:
                                      asset?.blockToFloorId != null
                                        ? "pointer"
                                        : "not-allowed",
                                    opacity:
                                      asset?.blockToFloorId != null ? 1 : 0.5,
                                    color:
                                      asset?.blockToFloorId != null
                                        ? "inherit"
                                        : "#999",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (asset?.blockToFloorId != null) {
                                      navigateToFloorplanView(asset);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : noResult && !showSearchBox ? (
                  <div
                    style={{
                      marginTop: "1rem",
                      backgroundColor: "#ffffff",
                      height: `40px`,
                      overflowY: "auto",
                      position: "absolute",
                      top: 40,
                      left: 10,
                      zIndex: 999,
                      width: "95%",
                      borderBottomRightRadius: "15px",
                      borderBottomLeftRadius: "15px",
                      paddingLeft: "1rem",
                    }}
                  >
                    <strong>No result found</strong>
                  </div>
                ) : null}
              </div>

              {/* {(selectedAssets && selectedAssets.length > 0) && ( */}
              <div className="d-flex gap-2"
              >
                <B2Button
                  variant="primary"
                  onClick={handleOpenSelectionModal}
                >
                  <FontAwesomeIcon icon={faList} className="me-2" />
                  Selection List
                </B2Button>
                <B2Button
                  variant="primary"
                  onClick={handleOpenAnalyticsModal}
                  disabled={selectedAssets?.length === 0}
                >
                  <FontAwesomeIcon icon={faChartBar} className="me-2" />
                  View Analytics
                </B2Button>
              </div>
              {/* )} */}
            </div>

            <div className="asset-main-layout mt-5">
              {/* filter section: left side */}
              <AssetMainFilterComponent
                getSelectedFilterInfo={getSelectedFilterInfo}
                assetsByBlockResponse={mergedAssetsData}
                onLevelOptionsChange={setLevelOptions}
              />

              {/* accordion tree section: right side */}
              <div className="p-3 pe-0 accordion-bg assetmainaccordion asset-main-accordion-col">
                {tCSelected.tcCode?.length ? (
                  <SkeletonComponent
                    wrapperHeight="450px"
                    skeletonHeight="65px"
                    isLoading={isInitialAssetsLoading || isLoadingBlocksBySiteCode}
                    count={7}
                  >
                    <AssetBreadcrumb
                      tcName={tCSelected.tcName}
                      siteName={filterSelectedSite?.siteName}
                      blockName={blocksBySiteCodeResponse?.find((b: any) => b.blockCode === blockSelected)?.blockName}
                      levelName={filterSelectedLevel?.levelName}
                    />

                    <B2Accordion
                      key={`block-accordion-${blockSelected || "none"}`}
                      alwaysOpen={false}
                      activeKey={blockSelected ? `block-${blockSelected}` : null}
                      onSelect={(eventKey: string) => {
                        if (eventKey) {
                          const blockCode = eventKey.replace('block-', '');
                          const clickedBlock = blocksBySiteCodeResponse?.find((b: any) => b.blockCode === blockCode);
                          if (clickedBlock) {
                            setSelectedLevel("");
                            setFilterSelectedLevel({ levelCode: null, levelName: null });
                            onClickedBlock(clickedBlock);
                          }
                        }
                      }}
                    >
                      {blocksBySiteCodeResponse?.map((block: any, j: any) => (
                        <B2Accordion.Item
                          eventKey={`block-${block.blockCode ?? j}`}
                          key={j}
                        >
                          <B2Accordion.Header>
                            {block?.blockName}
                          </B2Accordion.Header>
                          <B2Accordion.Body>
                            <AssetAccordionBody
                              isLoading={isInitialAssetsLoading}
                              mergedAssetsData={mergedAssetsData}
                              pageSize={PageSize}
                              isLoadingSelectedAssets={
                                isLoadingSelectedAssetsByBlockAndTypeData
                              }
                              onAssetSelect={(assetType) =>
                                dispatch(
                                  setSelectedAssetType(assetType)
                                )
                              }
                              onClickViewMore={onClickViewMore}
                              navigateToFault={navigateToFault}
                              selectedTC={tCSelected}
                              selectedSiteCode={filterSelectedSite?.siteCode}
                              selectedBlock={block}
                              SortBy={SortBy}
                              SortDirection={SortDirection}
                              selectedLevel={selectedLevel}
                              selectedAssets={selectedAssets}
                              onToggleAsset={handleToggleAsset}
                              allLevels={levelOptions}
                              onLevelSelect={handleLevelSelect}
                              onLoadMore={fetchNextPage}
                              hasMore={hasMoreAssetsByType}
                              isLoadingMore={isLoadingMoreAssetsByType}
                              levelAccordionReset={levelAccordionReset}
                            />
                          </B2Accordion.Body>
                        </B2Accordion.Item>
                      ))}
                    </B2Accordion>
                  </SkeletonComponent>
                ) : (
                  <div className="flex justify-center items-center">
                    <div
                      className="flex flex-col justify-center items-center"
                      style={{
                        width: "60%",
                        padding: "5rem",
                        borderRadius: "8px",
                        backgroundColor: "#EBEBEB",
                      }}
                    >
                      <div style={{ fontSize: "70px" }}>
                        <FontAwesomeIcon icon={faClipboard} className="mb-3" />
                      </div>
                      <h4>Please select a "Town Council" first</h4>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {assetGroupModalOpen && (
          <ModalComponent
            showModal={assetGroupModalOpen}
            handleClose={handleCloseModal}
            headerTitle={`${assetTypeSelected == "Luminarie" ? "Luminaire" : assetTypeSelected
              }`}
            isViewMode={true}
            size="xl"
          >
            <div>
              <B2Form onSubmit={handleSearch}>
                <div
                  className=""
                  style={{
                    display: "flex",
                    width: "300px",
                    marginLeft: "48rem",
                    marginBottom: "20px",
                  }}
                >
                  <B2InputGroup>
                    <FormControl
                      style={{
                        borderRight: searchValue && "none",
                        height: "45px",
                        width: "250px",
                      }}
                      className="search-field"
                      placeholder={"Search Assets"}
                      value={searchValue}
                      onChange={(e: any) => setSearchValue(e.target.value)}
                      onKeyUp={handleKeyUp}
                    />

                    {/* <B2Input.Icon icon="bi-person" prefix /> */}

                    <B2InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </B2InputGroup.Text>
                  </B2InputGroup>
                </div>
              </B2Form>
            </div>
            <div>
              <TableComponent
                tableHeaders={columns}
                data={selectedAssetsByBlockAndType}
                pageSize={PageSize}
                current={PageNo}
                totalNumberOfRecords={totalCount}
                onChangePage={handleChangePage}
                onChangeSort={handleChangeSort}
                isLoadingData={isLoadingSelectedAssetsByBlockAndTypeData}
                wrapperHeight="400px"
                count={6}
              />
            </div>
          </ModalComponent>
        )}

        <AssetSelectionModal
          showModal={isSelectionModalOpen}
          handleClose={handleCloseSelectionModal}
          selectedAssets={selectedAssets}
          onRemoveAsset={handleRemoveAsset}
          onClearAll={handleClearAllAssets}
          onViewAnalytics={handleOpenAnalyticsModal}
          currentPage={selectionPage}
          pageSize={selectionPageSize}
          onPageChange={setSelectionPage}
        />

        <AssetAnalyticsModal
          showModal={isAnalyticsModalOpen}
          handleClose={handleCloseAnalyticsModal}
          selectedAssets={selectedAssets}
          analyticsDate={analyticsDate}
          onDateChange={handleAnalyticsDateChange}
          onCompare={handleAnalyticsCompare}
        />
      </div>
    </>
  );
};

export default AssetMainComponent;
